import {
  AccessToken,
  DefaultTokenStore,
  IdToken,
  type Logger,
  type TokenStore,
} from "@meiden-connect/auth-frontend-sdk";
import {
  resolveStoredMeidenConnectAccessToken,
  withAuthOperationTimeout,
} from "@osakabe/auth-service-provider";
import type { SessionSnapshot } from "./types";
import { isSignedOut } from "./storage";

/**
 * `restoreSessionSnapshotFromMeidenConnect` の戻り値。
 *
 * - `session`: 保存済みトークンから SessionSnapshot を組み立てられた場合。
 * - `rate-limited`: refresh 中に MEIDEN CONNECT IdP から 429 応答を受けた場合。
 *   可視 SDK Provider 経路へフォールバックすると IdP の連続失敗カウンタを
 *   更に押し上げる恐れがあるため、呼び出し元で専用案内へ切り替える。
 * - `none`: トークンが見つからない、または復元できなかった場合。
 */
export type RestoreSessionSnapshotResult =
  | { readonly status: "session"; readonly snapshot: SessionSnapshot }
  | { readonly status: "rate-limited"; readonly retryAfterSeconds: number | undefined }
  | { readonly status: "none" };

const silentLogger: Logger = {
  debug: () => undefined,
  error: () => undefined,
  info: () => undefined,
  warn: () => undefined,
};

interface TokenStoreLike extends Pick<
  TokenStore,
  "readTokenSet" | "readUserIds" | "deleteTokenSet"
> {
  readonly close?: (() => Promise<void>) | undefined;
}

type TokenStoreFactory = (input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}) => TokenStoreLike;

let createTokenStore: TokenStoreFactory = ({ authServiceOrigin, serviceOrigin }) =>
  new DefaultTokenStore({
    authServiceOrigin,
    logger: silentLogger,
    serviceOrigin,
  });

/**
 * SDK の `MeidenConnectCredentialsProvider` をマウントする前に、IndexedDB に保存済みの
 * トークンから SessionSnapshot を組み立てる。
 *
 * これにより、アクセストークンの期限切れ判定や refresh が必要な場合でも、メイン UI を
 * Suspense でブロックせず、キャッシュ済みのユーザー情報で先行描画できる。
 *
 * 保存済みアクセストークンが期限切れであっても、refresh token が有効なら直接 refresh
 * 経路で再取得を試みる。これに失敗 (refresh token も期限切れ等) した場合のみ Snapshot
 * を破棄し、可視 SDK Provider 経路でサインインを促す。
 */
export async function restoreSessionSnapshotFromMeidenConnect(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}): Promise<RestoreSessionSnapshotResult> {
  if (isSignedOut()) {
    // 明示的なサインアウト後は、削除に失敗した残存トークンから復元しない。
    return { status: "none" };
  }

  const tokenStore = createTokenStoreBestEffort({
    authServiceOrigin: input.authServiceOrigin,
    serviceOrigin: input.serviceOrigin,
  });
  if (tokenStore === undefined) {
    return { status: "none" };
  }

  try {
    const userIds = await withAuthOperationTimeout(
      "session-bootstrap-read-user-ids",
      tokenStore.readUserIds()
    );
    if (userIds.length === 0) {
      return { status: "none" };
    }

    // SDK の `TokenStore.readUserIds()` は順序が実装定義のため、先頭要素を最新利用者と
    // 決めつけると複数アカウントが残っている環境で誤ったユーザーの SessionSnapshot を復元する
    // 恐れがある。
    //
    // 1) まず全 userId の tokenSet を読み、それぞれの idToken `iat` を取得する (refresh は走らせない)。
    // 2) アクセストークンが期限内のものがあれば、その中で最新の iat の tokenSet を採用する。
    // 3) 期限切れしかない場合は、最新 iat の userId に対してのみ refresh を 1 回だけ試みる。
    //    古い userId が複数残っていても、refresh の直列実行で起動が遅延するのを避けるための
    //    制約。最新 iat の refresh に失敗した場合は復元しない (= 可視 SDK Provider 経路に任せる)。
    const candidates: ReadonlyArray<{
      readonly userId: string;
      readonly tokenSet: { accessToken: string; idToken: string; refreshToken: string };
      readonly idTokenIat: number;
      readonly accessTokenAlive: boolean;
    }> = (
      await Promise.all(
        userIds.map(async (userId) => {
          const tokenSet = await withAuthOperationTimeout(
            "session-bootstrap-read-token-set",
            tokenStore.readTokenSet(userId, [input.serviceOrigin])
          ).catch(() => undefined);
          if (tokenSet === undefined) {
            return undefined;
          }
          return {
            userId,
            tokenSet,
            idTokenIat: readIdTokenIat(tokenSet.idToken),
            accessTokenAlive: isAccessTokenAlive(tokenSet.accessToken),
          };
        })
      )
    ).filter((entry): entry is Exclude<typeof entry, undefined> => entry !== undefined);
    if (candidates.length === 0) {
      return { status: "none" };
    }

    let bestTokenSet:
      | {
          readonly idTokenIat: number;
          readonly tokenSet: { accessToken: string; idToken: string; refreshToken: string };
        }
      | undefined;
    let refreshRateLimit: { readonly retryAfterSeconds: number | undefined } | undefined;
    const aliveCandidates = candidates.filter((entry) => entry.accessTokenAlive);
    if (aliveCandidates.length > 0) {
      // 期限内アクセストークンを持つ候補の中から、最新 iat の 1 件を採用する。
      const latestAlive = aliveCandidates.reduce((max, entry) =>
        entry.idTokenIat > max.idTokenIat ? entry : max
      );
      bestTokenSet = { idTokenIat: latestAlive.idTokenIat, tokenSet: latestAlive.tokenSet };
    } else {
      // 期限切れしか無い場合は、最新 iat の userId だけ refresh を試みる対象として 1 件選ぶ。
      const latestExpired = candidates.reduce((max, entry) =>
        entry.idTokenIat > max.idTokenIat ? entry : max
      );
      // refresh 失敗時に rate-limit を別経路で区別したいので catch ですべて握りつぶさず、
      // HTTP エラー応答だけは分類のために観測する。
      let refreshedAccessToken: string | undefined;
      try {
        refreshedAccessToken = await resolveStoredMeidenConnectAccessToken({
          authServiceOrigin: input.authServiceOrigin,
          forceRefresh: true,
          serviceOrigin: input.serviceOrigin,
          serviceOrigins: [input.serviceOrigin],
          viewerUserId: latestExpired.userId,
        });
      } catch (refreshError) {
        const status = readHttpErrorStatus(refreshError);
        if (status === 429) {
          // MEIDEN CONNECT IdP がレートリミット中。SDK Provider の可視 fallback 経路へ
          // フォールバックすると更に再認可要求が走り、ブロック時間を延ばす恐れがある。
          refreshRateLimit = { retryAfterSeconds: readRetryAfterSeconds(refreshError) };
        }
        refreshedAccessToken = undefined;
      }
      if (refreshedAccessToken !== undefined) {
        const refreshedTokenSet = await withAuthOperationTimeout(
          "session-bootstrap-read-refreshed-token-set",
          tokenStore.readTokenSet(latestExpired.userId, [input.serviceOrigin])
        ).catch(() => undefined);
        if (refreshedTokenSet !== undefined && isAccessTokenAlive(refreshedTokenSet.accessToken)) {
          bestTokenSet = {
            idTokenIat: readIdTokenIat(refreshedTokenSet.idToken),
            tokenSet: refreshedTokenSet,
          };
        }
      }
    }
    if (bestTokenSet === undefined) {
      if (refreshRateLimit !== undefined) {
        return {
          status: "rate-limited",
          retryAfterSeconds: refreshRateLimit.retryAfterSeconds,
        };
      }
      return { status: "none" };
    }

    const idToken = new IdToken(bestTokenSet.tokenSet.idToken);
    const accessToken = new AccessToken(bestTokenSet.tokenSet.accessToken, input.serviceOrigin);

    const tokenPermissions = readPermissionIds(accessToken.globalPermissionIds);
    const idTokenPermissions = readPermissionIds(
      (idToken as { readonly globalPermissionIds?: unknown }).globalPermissionIds
    );
    const mergedPermissions = [...new Set([...tokenPermissions, ...idTokenPermissions])];

    return {
      status: "session",
      snapshot: {
        userId: idToken.userId,
        name: idToken.name,
        email: typeof idToken.email === "string" ? idToken.email : undefined,
        // SDK は IdToken に `department` を載せることがあるため、それを `affiliation` として採用する。
        affiliation: readAffiliationFromIdToken(idToken),
        avatarImageUrl:
          typeof idToken.iconUrl === "string" && idToken.iconUrl.length > 0
            ? idToken.iconUrl
            : undefined,
        source: "meiden-connect",
        globalPermissionIds: mergedPermissions,
      },
    };
  } catch {
    return { status: "none" };
  } finally {
    await closeTokenStoreBestEffort(tokenStore, "session-bootstrap-token-store-close");
  }
}

/** ID トークンの `iat` (issued at, epoch seconds) を取り出す。読めない場合は 0 を返す。 */
function readIdTokenIat(idToken: string): number {
  try {
    const segments = idToken.split(".");
    const payloadSegment = segments[1];
    if (payloadSegment === undefined) {
      return 0;
    }
    const normalizedSegment = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const paddedSegment = normalizedSegment.padEnd(
      Math.ceil(normalizedSegment.length / 4) * 4,
      "="
    );
    const parsed = JSON.parse(atob(paddedSegment));
    const iat =
      typeof parsed.iat === "number"
        ? parsed.iat
        : typeof parsed.iat === "string"
          ? Number(parsed.iat)
          : 0;
    return typeof iat === "number" && Number.isFinite(iat) ? iat : 0;
  } catch {
    return 0;
  }
}

/**
 * SDK / 認証サービス共通 HTTP エラーから `status` 値を取り出します。
 *
 * MEIDEN CONNECT SDK の `HttpError` は `status` プロパティに HTTP ステータスを
 * 保持しているが、同じ形のエラーが将来的に差し替えられた場合に備えて
 * `statusCode` 名も併せて参照する duck typing 実装にしている。
 */
function readHttpErrorStatus(error: unknown): number | undefined {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const candidate = error as Error & {
    readonly status?: unknown;
    readonly statusCode?: unknown;
  };
  if (typeof candidate.status === "number") {
    return candidate.status;
  }
  if (typeof candidate.statusCode === "number") {
    return candidate.statusCode;
  }
  return undefined;
}

/**
 * HTTP エラーに含まれる `Retry-After` ヘッダー値を秒として読み取ります。
 *
 * 値が読み取れない、または 0 以下の場合は undefined を返す。
 */
function readRetryAfterSeconds(error: unknown): number | undefined {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const headers = (error as Error & { readonly headers?: unknown }).headers;
  const rawValue = readRetryAfterHeaderValue(headers);
  if (rawValue === undefined) {
    return undefined;
  }
  const parsed = Number.parseInt(rawValue, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function readRetryAfterHeaderValue(headers: unknown): string | undefined {
  if (headers === null || headers === undefined) {
    return undefined;
  }
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    const value = headers.get("Retry-After");
    return value !== null && value.length > 0 ? value : undefined;
  }
  if (typeof headers !== "object") {
    return undefined;
  }
  const headerRecord = headers as Record<string, unknown>;
  const rawValue = headerRecord["Retry-After"] ?? headerRecord["retry-after"];
  return typeof rawValue === "string" && rawValue.length > 0 ? rawValue : undefined;
}

/**
 * IndexedDB の MEIDEN CONNECT token store から、対象 origin に保存されている全 userId のトークンセットを削除する。
 *
 * SDK サブツリーが停止していて `user.signOut()` を呼べない場合の best-effort なログアウト経路から使用する想定。
 * これを忘れると localStorage / React state だけクリアしても、後で SDK が再マウントされたタイミングで
 * IndexedDB の残存トークンから自動再ログインしてしまう。
 */
export async function purgeAllMeidenConnectTokens(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}): Promise<{ readonly remainingUserIds: readonly string[] }> {
  const tokenStore = createTokenStoreBestEffort({
    authServiceOrigin: input.authServiceOrigin,
    serviceOrigin: input.serviceOrigin,
  });
  if (tokenStore === undefined) {
    return { remainingUserIds: ["unknown"] };
  }
  try {
    const firstUserIds = await withAuthOperationTimeout(
      "session-bootstrap-purge-read-user-ids",
      tokenStore.readUserIds()
    );
    await deleteTokenSets(tokenStore, firstUserIds);

    const remainingAfterFirstAttempt = await withAuthOperationTimeout(
      "session-bootstrap-purge-read-remaining-user-ids",
      tokenStore.readUserIds()
    );
    if (remainingAfterFirstAttempt.length === 0) {
      return { remainingUserIds: [] };
    }

    await deleteTokenSets(tokenStore, remainingAfterFirstAttempt);
    const remainingUserIds = await withAuthOperationTimeout(
      "session-bootstrap-purge-read-final-user-ids",
      tokenStore.readUserIds()
    );
    return { remainingUserIds };
  } catch {
    // 残存を確認できない場合は安全側の印を返し、サインアウト遷移自体は止めない。
    return { remainingUserIds: ["unknown"] };
  } finally {
    await closeTokenStoreBestEffort(tokenStore, "session-bootstrap-purge-token-store-close");
  }
}

async function deleteTokenSets(
  tokenStore: TokenStoreLike,
  userIds: readonly string[]
): Promise<void> {
  for (const userId of userIds) {
    await withAuthOperationTimeout(
      "session-bootstrap-purge-delete-token-set",
      tokenStore.deleteTokenSet(userId)
    ).catch(() => undefined);
  }
}

/**
 * 指定した利用者の ID トークンを読み出す。
 *
 * 対応するトークンが無い場合も別利用者へフォールバックしない。
 */
export async function readMeidenConnectIdToken(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
  readonly userId: string;
}): Promise<string | undefined> {
  const tokenStore = createTokenStoreBestEffort({
    authServiceOrigin: input.authServiceOrigin,
    serviceOrigin: input.serviceOrigin,
  });
  if (tokenStore === undefined) {
    return undefined;
  }
  try {
    const tokenSet = await withAuthOperationTimeout(
      "session-bootstrap-read-sign-out-id-token",
      tokenStore.readTokenSet(input.userId, [input.serviceOrigin])
    ).catch(() => undefined);
    return tokenSet?.idToken || undefined;
  } finally {
    await closeTokenStoreBestEffort(
      tokenStore,
      "session-bootstrap-read-sign-out-token-store-close"
    );
  }
}

/**
 * TokenStore の生成失敗を呼び出し元へ伝播させず、認証情報を安全側に扱う。
 *
 * IndexedDB を利用できないブラウザー設定や不正な origin でも、サインアウト処理そのものを
 * reject させないための境界である。
 */
function createTokenStoreBestEffort(input: {
  readonly authServiceOrigin: string;
  readonly serviceOrigin: string;
}): TokenStoreLike | undefined {
  try {
    return createTokenStore(input);
  } catch {
    return undefined;
  }
}

/** TokenStore の close が同期・非同期のどちらで失敗しても処理結果を維持する。 */
async function closeTokenStoreBestEffort(
  tokenStore: TokenStoreLike,
  operationName: string
): Promise<void> {
  try {
    const closePromise = tokenStore.close?.();
    if (closePromise !== undefined) {
      await withAuthOperationTimeout(operationName, closePromise, 1_000).catch(() => undefined);
    }
  } catch {
    // close は後片付けなので、処理本体の結果を上書きしない。
  }
}

/** アクセストークンが現時点でまだ有効か (30 秒の猶予つき) を JWT パース無しで簡易判定する */
function isAccessTokenAlive(accessToken: string | undefined): boolean {
  if (typeof accessToken !== "string" || accessToken.length === 0) {
    return false;
  }

  try {
    const segments = accessToken.split(".");
    const payloadSegment = segments[1];
    if (payloadSegment === undefined) {
      return false;
    }

    const normalizedSegment = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const paddedSegment = normalizedSegment.padEnd(
      Math.ceil(normalizedSegment.length / 4) * 4,
      "="
    );
    const parsed = JSON.parse(atob(paddedSegment));
    const exp =
      typeof parsed.exp === "number"
        ? parsed.exp
        : typeof parsed.exp === "string"
          ? Number(parsed.exp)
          : undefined;

    if (typeof exp !== "number" || !Number.isFinite(exp)) {
      return false;
    }

    return exp > Date.now() / 1000 + 30;
  } catch {
    return false;
  }
}

function readPermissionIds(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === "string");
}

function readAffiliationFromIdToken(idToken: IdToken): string | undefined {
  // IdToken は SDK 内部で `department` 等のオプショナル項目を持つことがある。
  // 公開型に依存しすぎないよう unknown 経由で安全に読み出す。
  const candidate = idToken as unknown as {
    readonly department?: unknown;
    readonly affiliation?: unknown;
  };
  const department =
    typeof candidate.department === "string" && candidate.department.length > 0
      ? candidate.department
      : undefined;
  if (department !== undefined) {
    return department;
  }
  const affiliation =
    typeof candidate.affiliation === "string" && candidate.affiliation.length > 0
      ? candidate.affiliation
      : undefined;
  return affiliation;
}

/**
 * テスト用にトークンストア生成処理を差し替える。
 */
export function setSessionBootstrapTokenStoreFactoryForTest(factory?: TokenStoreFactory): void {
  createTokenStore =
    factory ??
    (({ authServiceOrigin, serviceOrigin }) =>
      new DefaultTokenStore({
        authServiceOrigin,
        logger: silentLogger,
        serviceOrigin,
      }));
}
